/**
 * Every request the app makes to Supabase carries a deadline.
 *
 * Without one, a request that is written onto a socket the network has already
 * dropped sends nothing and answers nothing: it ends when the kernel gives up
 * retransmitting, about two minutes later. React Native's HTTP client is built
 * with connect, read and write timeouts of zero, and neither supabase-js nor
 * auth-js attaches a signal of its own, so nothing shorter ever intervenes.
 *
 * That is not merely one slow call. supabase-js asks auth-js for the access
 * token before every PostgREST, Storage and Functions request, and on React
 * Native auth-js has no real lock - it queues every caller behind whoever is
 * inside. The auto-refresh ticker fires its token POST from inside that queue,
 * so a single dead socket under a refresh stops every query in the app: a
 * screen draws its header and then waits, having sent nothing at all.
 *
 * The deadline turns that into an ordinary failed request. Screens already show
 * the error from a request that fails, so the person sees what happened and can
 * retry, instead of watching a spinner for minutes.
 */

/** A token refresh is a few hundred bytes each way; ten seconds is already far beyond a healthy one. */
export const AUTH_REQUEST_TIMEOUT_MS = 10_000;

/**
 * Data, Storage and Functions requests. The longest legitimate one is a
 * dictation capture, which uploads the recording base64-encoded in the request
 * body and waits for transcription; on a weak mobile uplink that is tens of
 * seconds. Forty-five seconds sits above that with room to spare, and is about
 * a third of the roughly two minutes a dead socket otherwise costs.
 */
export const REQUEST_TIMEOUT_MS = 45_000;

/** True for Supabase's own auth endpoints (`/auth/v1/...`), which carry the short deadline. */
function isAuthRequest(url: string): boolean {
  return url.includes('/auth/v1/');
}

/** The URL of a fetch argument, in each of the three shapes fetch accepts. */
function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

/**
 * A fetch that gives up on its own. Requests are made through `impl`, which is
 * read at call time rather than captured, so a test can put its own fetch in
 * front of the client and see what the client actually sends.
 *
 * A caller that passes its own `signal` keeps it: whichever fires first ends
 * the request.
 */
export function createBoundedFetch(
  impl: () => typeof fetch,
  timeouts: { authMs: number; requestMs: number } = {
    authMs: AUTH_REQUEST_TIMEOUT_MS,
    requestMs: REQUEST_TIMEOUT_MS,
  },
): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const ms = isAuthRequest(urlOf(input)) ? timeouts.authMs : timeouts.requestMs;
    const controller = new AbortController();
    const callerSignal = init?.signal;
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort();
      } else {
        callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }
    let ranOut = false;
    const timer = setTimeout(() => {
      ranOut = true;
      controller.abort();
    }, ms);
    try {
      return await impl()(input, { ...init, signal: controller.signal });
    } catch (failure) {
      // Say what happened. Aborting is how the request is stopped, but "aborted"
      // is not the reason, and the reason is what a screen shows the person.
      throw ranOut ? new Error(`the server did not answer within ${Math.round(ms / 1000)} seconds`) : failure;
    } finally {
      clearTimeout(timer);
    }
  };
}
